import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, uploadFileToSignedUrl } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { Field, PasswordInput, Select, TextArea, TextInput } from '../components/Field.tsx';
import { StringListEditor } from '../components/StringListEditor.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { VerticalTabs } from '../components/VerticalTabs.tsx';
import { AdminUsers } from './Users.tsx';
import { AdminActivity } from './Activity.tsx';
import { useBranding } from '../lib/branding.tsx';
import { useConfirm } from '../lib/confirm.tsx';
import { COMPANY_INFO_LABEL_DEFAULTS, type Branding } from '@l2u/shared/schemas';
import type { QuestionCategory } from '../lib/types.ts';
import { type TeamRegion } from '../lib/teamRegions.ts';

const HEX_RE = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/;

const SETTINGS_TABS = [
  { key: 'users', label: 'Users' },
  { key: 'activity', label: 'Activity' },
  { key: 'brand', label: 'Brand' },
  { key: 'company-info', label: 'Company Info' },
  { key: 'team-regions', label: 'Team Regions' },
  { key: 'question-categories', label: 'Question Categories' },
  { key: 'integrations', label: 'Integrations' },
] as const;
type TabKey = (typeof SETTINGS_TABS)[number]['key'];
const TAB_KEYS: string[] = SETTINGS_TABS.map((t) => t.key);

export function AdminSettings() {
  const qc = useQueryClient();
  const { reload } = useBranding();
  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => adminApi.get<{ branding: Partial<Branding> }>('/api/settings'),
  });

  const [form, setForm] = useState<Partial<Branding>>({});
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (data) setForm({ ...data.branding });
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (input: Partial<Branding>) => adminApi.put('/api/settings', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void reload();
    },
    onError: (err) => setError(err),
  });

  const logoMutation = useMutation({
    mutationFn: async (file: File) => {
      const presigned = await adminApi.post<{ url: string; public_url: string }>(
        '/api/settings/logo-upload-url',
        { file_name: file.name, mime_type: file.type },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      return presigned.public_url;
    },
    onSuccess: (publicUrl) => {
      setForm((f) => ({ ...f, logoUrl: publicUrl }));
    },
    onError: (err) => setError(err),
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') ?? '';
  const activeTab: TabKey = (TAB_KEYS.includes(tabParam) ? tabParam : 'users') as TabKey;
  const setActiveTab = (key: string) =>
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set('tab', key);
        return p;
      },
      { replace: true },
    );

  if (isLoading) return <CenteredSpinner />;

  function set<K extends keyof Branding>(key: K, value: Branding[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function validate(): string | null {
    if (form.primaryColor && !HEX_RE.test(form.primaryColor))
      return 'Primary colour must be a hex value';
    if (form.primaryDark && !HEX_RE.test(form.primaryDark))
      return 'Primary dark must be a hex value';
    if (form.accentColor && !HEX_RE.test(form.accentColor))
      return 'Accent colour must be a hex value';
    return null;
  }

  function save() {
    const err = validate();
    if (err) {
      setError(new Error(err));
      return;
    }
    setError(null);
    saveMutation.mutate(form);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">Manage portal configuration</p>
      </div>
      <div className="flex gap-6">
        <VerticalTabs
          tabs={SETTINGS_TABS}
          active={activeTab}
          onSelect={setActiveTab}
          ariaLabel="Settings sections"
        />
        <div
          className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white p-6"
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
        >
          {activeTab === 'users' && <AdminUsers />}
          {activeTab === 'activity' && <AdminActivity />}
          {activeTab === 'brand' && (
            <BrandTab
              form={form}
              set={set}
              save={save}
              saving={saveMutation.isPending}
              logoMutation={logoMutation}
              error={error}
            />
          )}
          {activeTab === 'company-info' && (
            <div className="space-y-4">
              <CompanyInfoSettingsPanel form={form} set={set} />
              <div className="flex justify-end">
                <Btn onClick={save} loading={saveMutation.isPending}>
                  Save
                </Btn>
              </div>
            </div>
          )}
          {activeTab === 'team-regions' && <TeamRegionsPanel />}
          {activeTab === 'question-categories' && <QuestionCategoriesPanel />}
          {activeTab === 'integrations' && <LocateIntegrationPanel />}
        </div>
      </div>
    </div>
  );
}

function BrandTab({
  form,
  set,
  save,
  saving,
  logoMutation,
  error,
}: {
  form: Partial<Branding>;
  set: <K extends keyof Branding>(key: K, value: Branding[K]) => void;
  save: () => void;
  saving: boolean;
  logoMutation: { isPending: boolean; mutate: (file: File) => void };
  error: unknown;
}) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Brand</h2>
        <Btn onClick={save} loading={saving}>
          Save
        </Btn>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Identity</h2>
          <Field label="Company name">
            <TextInput
              value={form.companyName ?? ''}
              onChange={(e) => set('companyName', e.target.value)}
            />
          </Field>
          <Field label="Tagline">
            <TextInput
              value={form.tagline ?? ''}
              onChange={(e) => set('tagline', e.target.value)}
            />
          </Field>
          <Field label="Logo">
            <div className="flex items-center gap-3">
              {form.logoUrl && (
                <img
                  src={form.logoUrl}
                  alt="Logo"
                  className="h-10 w-auto rounded border border-slate-200"
                />
              )}
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) logoMutation.mutate(f);
                }}
                className="text-sm"
                disabled={logoMutation.isPending}
              />
              {logoMutation.isPending && <span className="text-xs text-slate-500">Uploading…</span>}
            </div>
          </Field>

          <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Colours
          </h2>
          <div className="grid grid-cols-3 gap-3">
            <ColourField
              label="Primary"
              value={form.primaryColor}
              onChange={(v) => set('primaryColor', v)}
            />
            <ColourField
              label="Primary dark"
              value={form.primaryDark}
              onChange={(v) => set('primaryDark', v)}
            />
            <ColourField
              label="Accent"
              value={form.accentColor}
              onChange={(v) => set('accentColor', v)}
            />
          </div>

          <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Welcome screen
          </h2>
          <Field label="Heading">
            <TextInput
              value={form.welcomeHeading ?? ''}
              onChange={(e) => set('welcomeHeading', e.target.value)}
            />
          </Field>
          <Field label="Message">
            <TextArea
              value={form.welcomeMessage ?? ''}
              onChange={(e) => set('welcomeMessage', e.target.value)}
            />
          </Field>

          <ErrorMessage error={error} />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Preview
          </h2>
          <div
            className="rounded-lg p-6 text-white"
            style={{ backgroundColor: form.primaryColor ?? '#2563EB' }}
          >
            {form.logoUrl && <img src={form.logoUrl} alt="" className="mb-3 h-10 w-auto" />}
            <div className="text-lg font-semibold">{form.companyName ?? 'Company name'}</div>
            {form.tagline && <div className="text-sm opacity-80">{form.tagline}</div>}
          </div>
          <div className="mt-4 rounded-lg border border-slate-200 p-4">
            <div className="text-base font-semibold text-slate-900">
              {form.welcomeHeading ?? 'Welcome heading'}
            </div>
            <div className="mt-1 text-sm text-slate-600">
              {form.welcomeMessage ?? 'Welcome message'}
            </div>
            <button
              className="mt-3 rounded-md px-3 py-1.5 text-sm font-medium text-white"
              style={{ backgroundColor: form.accentColor ?? '#3B82F6' }}
            >
              Sample button
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompanyInfoSettingsPanel({
  form,
  set,
}: {
  form: Partial<Branding>;
  set: <K extends keyof Branding>(key: K, value: Branding[K]) => void;
}) {
  const labels = form.companyInfoLabels ?? {};
  const contractorTypes = form.contractorTypeOptions ?? [];

  function setLabel(key: keyof typeof COMPANY_INFO_LABEL_DEFAULTS, value: string) {
    const trimmed = value.trim();
    const next = { ...(form.companyInfoLabels ?? {}) };
    if (trimmed.length === 0) {
      delete (next as Record<string, unknown>)[key];
    } else {
      (next as Record<string, string>)[key] = trimmed;
    }
    set('companyInfoLabels', Object.keys(next).length === 0 ? undefined : next);
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Company information
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Rename the labels shown on the admin invite form and the company&apos;s questionnaire.
          Leave blank to use the default label.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {(
          Object.entries(COMPANY_INFO_LABEL_DEFAULTS) as [
            keyof typeof COMPANY_INFO_LABEL_DEFAULTS,
            string,
          ][]
        ).map(([key, fallback]) => (
          <Field key={key} label={`${key} label`} hint={`Default: ${fallback}`}>
            <TextInput
              value={labels[key] ?? ''}
              placeholder={fallback}
              onChange={(e) => setLabel(key, e.target.value)}
            />
          </Field>
        ))}
      </div>

      <Field
        label="Contractor type options"
        hint="These become the audience options on questionnaires and the choices on the new-company form."
      >
        <StringListEditor
          value={contractorTypes}
          onChange={(list) => set('contractorTypeOptions', list.length === 0 ? undefined : list)}
          itemLabel="contractor type"
          addLabel="Add contractor type"
          placeholder="e.g. Owner Driver"
          maxLength={120}
        />
      </Field>
    </div>
  );
}

function QuestionCategoriesPanel() {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data, isLoading } = useQuery({
    queryKey: ['question-categories'],
    queryFn: () =>
      adminApi.get<{ categories: QuestionCategory[] }>('/api/question-categories'),
  });

  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [error, setError] = useState<unknown>(null);

  const categories = data?.categories ?? [];

  const createMutation = useMutation({
    mutationFn: (input: { name: string }) =>
      adminApi.post('/api/question-categories', input),
    onSuccess: () => {
      setName('');
      void qc.invalidateQueries({ queryKey: ['question-categories'] });
    },
    onError: (err) => setError(err),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      adminApi.patch(`/api/question-categories/${input.id}`, { name: input.name }),
    onSuccess: () => {
      setEditingId(null);
      setEditingName('');
      void qc.invalidateQueries({ queryKey: ['question-categories'] });
    },
    onError: (err) => setError(err),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/question-categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['question-categories'] }),
    onError: (err) => setError(err),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => adminApi.post('/api/question-categories/reorder', { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['question-categories'] }),
    onError: (err) => setError(err),
  });

  function move(id: string, dir: -1 | 1) {
    const ids = categories.map((c) => c.id);
    const idx = ids.indexOf(id);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= ids.length) return;
    [ids[idx], ids[next]] = [ids[next]!, ids[idx]!];
    reorderMutation.mutate(ids);
  }

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Question categories
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Categories are shared across all questionnaires and group questions into wizard steps.
          Parent/child links may cross categories — pick any question in the same questionnaire as a
          parent when setting up conditional logic.
        </p>
      </div>

      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          const trimmed = name.trim();
          if (!trimmed) return;
          createMutation.mutate({ name: trimmed });
        }}
      >
        <div className="flex-1">
          <Field label="Add category">
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Asset safety"
            />
          </Field>
        </div>
        <Btn type="submit" loading={createMutation.isPending} disabled={!name.trim()}>
          Add
        </Btn>
      </form>

      {isLoading ? (
        <CenteredSpinner />
      ) : categories.length === 0 ? (
        <EmptyState
          title="No categories yet"
          description="Add a category above to start organising questions."
        />
      ) : (
        <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
          {categories.map((cat, i) => (
            <li key={cat.id} className="flex items-center gap-3 px-3 py-2">
              <div className="flex flex-col">
                <button
                  onClick={() => move(cat.id, -1)}
                  disabled={i === 0}
                  className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  aria-label="Move up"
                >
                  ▲
                </button>
                <button
                  onClick={() => move(cat.id, 1)}
                  disabled={i === categories.length - 1}
                  className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  aria-label="Move down"
                >
                  ▼
                </button>
              </div>
              <div className="flex-1">
                {editingId === cat.id ? (
                  <TextInput
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && editingName.trim()) {
                        updateMutation.mutate({ id: cat.id, name: editingName.trim() });
                      } else if (e.key === 'Escape') {
                        setEditingId(null);
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <span className="text-sm text-slate-900">{cat.name}</span>
                )}
              </div>
              {editingId === cat.id ? (
                <>
                  <Btn
                    size="sm"
                    onClick={() => updateMutation.mutate({ id: cat.id, name: editingName.trim() })}
                    loading={updateMutation.isPending}
                    disabled={!editingName.trim()}
                  >
                    Save
                  </Btn>
                  <Btn size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                    Cancel
                  </Btn>
                </>
              ) : (
                <>
                  <Btn
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setEditingId(cat.id);
                      setEditingName(cat.name);
                    }}
                  >
                    Rename
                  </Btn>
                  <Btn
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      void (async () => {
                        if (
                          await confirm({
                            title: 'Delete category',
                            body: (
                              <>
                                Are you sure you want to delete the category{' '}
                                <span className="font-semibold">{cat.name}</span>?
                              </>
                            ),
                            confirmLabel: 'Delete',
                            danger: true,
                          })
                        )
                          deleteMutation.mutate(cat.id);
                      })();
                    }}
                  >
                    Delete
                  </Btn>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <ErrorMessage error={error} />
    </div>
  );
}

function TeamRegionsPanel() {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data, isLoading } = useQuery({
    queryKey: ['team-regions'],
    queryFn: () => adminApi.get<{ team_regions: TeamRegion[] }>('/api/team-regions'),
  });

  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [error, setError] = useState<unknown>(null);

  const regions = data?.team_regions ?? [];

  const createMutation = useMutation({
    mutationFn: (input: { name: string }) => adminApi.post('/api/team-regions', input),
    onSuccess: () => {
      setName('');
      void qc.invalidateQueries({ queryKey: ['team-regions'] });
    },
    onError: (err) => setError(err),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      adminApi.patch(`/api/team-regions/${input.id}`, { name: input.name }),
    onSuccess: () => {
      setEditingId(null);
      setEditingName('');
      void qc.invalidateQueries({ queryKey: ['team-regions'] });
      void qc.invalidateQueries({ queryKey: ['companies'] });
      void qc.invalidateQueries({ queryKey: ['drivers'] });
      void qc.invalidateQueries({ queryKey: ['assets'] });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => setError(err),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/team-regions/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team-regions'] });
      void qc.invalidateQueries({ queryKey: ['companies'] });
      void qc.invalidateQueries({ queryKey: ['drivers'] });
      void qc.invalidateQueries({ queryKey: ['assets'] });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => setError(err),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => adminApi.post('/api/team-regions/reorder', { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-regions'] }),
    onError: (err) => setError(err),
  });

  function move(id: string, dir: -1 | 1) {
    const ids = regions.map((r) => r.id);
    const idx = ids.indexOf(id);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= ids.length) return;
    [ids[idx], ids[next]] = [ids[next]!, ids[idx]!];
    reorderMutation.mutate(ids);
  }

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Team regions
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Regions group vendors, drivers and assets for admin visibility. Renaming a region updates
          every company, driver and asset tagged with it; a region in use cannot be deleted.
        </p>
      </div>

      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          const trimmed = name.trim();
          if (!trimmed) return;
          createMutation.mutate({ name: trimmed });
        }}
      >
        <div className="flex-1">
          <Field label="Add team region">
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Metro North"
            />
          </Field>
        </div>
        <Btn type="submit" loading={createMutation.isPending} disabled={!name.trim()}>
          Add
        </Btn>
      </form>

      {isLoading ? (
        <CenteredSpinner />
      ) : regions.length === 0 ? (
        <EmptyState
          title="No team regions yet"
          description="Add a team region above to start organising companies."
        />
      ) : (
        <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
          {regions.map((region, i) => (
            <li key={region.id} className="flex items-center gap-3 px-3 py-2">
              <div className="flex flex-col">
                <button
                  onClick={() => move(region.id, -1)}
                  disabled={i === 0}
                  className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  aria-label="Move up"
                >
                  ▲
                </button>
                <button
                  onClick={() => move(region.id, 1)}
                  disabled={i === regions.length - 1}
                  className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  aria-label="Move down"
                >
                  ▼
                </button>
              </div>
              <div className="flex-1">
                {editingId === region.id ? (
                  <TextInput
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && editingName.trim()) {
                        updateMutation.mutate({ id: region.id, name: editingName.trim() });
                      } else if (e.key === 'Escape') {
                        setEditingId(null);
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <span className="text-sm text-slate-900">{region.name}</span>
                )}
              </div>
              {editingId === region.id ? (
                <>
                  <Btn
                    size="sm"
                    onClick={() =>
                      updateMutation.mutate({ id: region.id, name: editingName.trim() })
                    }
                    loading={updateMutation.isPending}
                    disabled={!editingName.trim()}
                  >
                    Save
                  </Btn>
                  <Btn size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                    Cancel
                  </Btn>
                </>
              ) : (
                <>
                  <Btn
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setEditingId(region.id);
                      setEditingName(region.name);
                    }}
                  >
                    Rename
                  </Btn>
                  <Btn
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      void (async () => {
                        if (
                          await confirm({
                            title: 'Delete team region',
                            body: (
                              <>
                                Are you sure you want to delete the team region{' '}
                                <span className="font-semibold">{region.name}</span>?
                              </>
                            ),
                            confirmLabel: 'Delete',
                            danger: true,
                          })
                        )
                          deleteMutation.mutate(region.id);
                      })();
                    }}
                  >
                    Delete
                  </Btn>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <ErrorMessage error={error} />
    </div>
  );
}

type Locate2uStatus = {
  connected: boolean;
  client_id: string | null;
  regions_synced_at: string | null;
  drivers_synced_at: string | null;
  last_error: string | null;
};

function maskClientId(id: string): string {
  return id.length <= 4 ? '••••••••' : `${id.slice(0, 4)}••••••••`;
}

function formatSyncedAt(value: string | null): string {
  return value ? new Date(value).toLocaleString() : 'never';
}

function LocateIntegrationPanel() {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data, isLoading } = useQuery({
    queryKey: ['integration', 'locate2u'],
    queryFn: () => adminApi.get<Locate2uStatus>('/api/settings/integrations/locate2u'),
  });

  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [overrideCompanyId, setOverrideCompanyId] = useState('');
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const connected = data?.connected ?? false;

  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      adminApi.get<{ companies: { id: string; company_name: string }[] }>('/api/companies'),
    enabled: connected,
  });

  const connectMutation = useMutation({
    mutationFn: (input: { client_id: string; client_secret: string }) =>
      adminApi.post('/api/settings/integrations/locate2u/connect', input),
    onSuccess: () => {
      setClientId('');
      setClientSecret('');
      setError(null);
      setSyncMessage(null);
      void qc.invalidateQueries({ queryKey: ['integration', 'locate2u'] });
    },
    onError: (err) => setError(err),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => adminApi.post('/api/settings/integrations/locate2u/disconnect'),
    onSuccess: () => {
      setError(null);
      setSyncMessage(null);
      void qc.invalidateQueries({ queryKey: ['integration', 'locate2u'] });
    },
    onError: (err) => setError(err),
  });

  const syncRegionsMutation = useMutation({
    mutationFn: () =>
      adminApi.post<{ created: number; updated: number }>(
        '/api/settings/integrations/locate2u/sync-team-regions',
      ),
    onSuccess: (r) => {
      setError(null);
      setSyncMessage(`Team regions synced: ${r.created} created, ${r.updated} updated.`);
      void qc.invalidateQueries({ queryKey: ['integration', 'locate2u'] });
      void qc.invalidateQueries({ queryKey: ['team-regions'] });
      // Region renames cascade to the name-keyed team_region columns.
      void qc.invalidateQueries({ queryKey: ['companies'] });
      void qc.invalidateQueries({ queryKey: ['drivers'] });
      void qc.invalidateQueries({ queryKey: ['assets'] });
    },
    onError: (err) => {
      setError(err);
      void qc.invalidateQueries({ queryKey: ['integration', 'locate2u'] });
    },
  });

  const syncDriversMutation = useMutation({
    mutationFn: () =>
      adminApi.post<{ created: number; updated: number; skipped_unmatched: number }>(
        '/api/settings/integrations/locate2u/sync-drivers',
        overrideCompanyId ? { override_company_id: overrideCompanyId } : {},
      ),
    onSuccess: (r) => {
      setError(null);
      setSyncMessage(
        `Drivers synced: ${r.created} created, ${r.updated} updated, ${r.skipped_unmatched} skipped (no matching company).`,
      );
      void qc.invalidateQueries({ queryKey: ['integration', 'locate2u'] });
      void qc.invalidateQueries({ queryKey: ['drivers'] });
    },
    onError: (err) => {
      setError(err);
      void qc.invalidateQueries({ queryKey: ['integration', 'locate2u'] });
    },
  });

  if (isLoading) return <CenteredSpinner />;

  const syncing = syncRegionsMutation.isPending || syncDriversMutation.isPending;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Locate2u</h2>
        <p className="mt-1 text-xs text-slate-500">
          Connect your Locate2u account to import team regions and drivers. Syncing never deletes
          anything in this portal.
        </p>
      </div>

      {!connected ? (
        <form
          className="max-w-md space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (!clientId.trim() || !clientSecret) return;
            connectMutation.mutate({ client_id: clientId.trim(), client_secret: clientSecret });
          }}
        >
          <Field label="Client ID">
            <TextInput
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="Locate2u client id"
            />
          </Field>
          <Field label="Client Secret">
            <PasswordInput
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder="Locate2u client secret"
            />
          </Field>
          <Btn
            type="submit"
            loading={connectMutation.isPending}
            disabled={!clientId.trim() || !clientSecret}
          >
            Connect
          </Btn>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
              Connected
            </span>
            {data?.client_id && (
              <span className="font-mono text-sm text-slate-600">
                {maskClientId(data.client_id)}
              </span>
            )}
          </div>

          <div className="space-y-3 rounded-lg border border-slate-200 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <Btn
                onClick={() => syncRegionsMutation.mutate()}
                loading={syncRegionsMutation.isPending}
                disabled={syncing}
              >
                Sync Team Regions
              </Btn>
              <span className="text-xs text-slate-500">
                Last synced: {formatSyncedAt(data?.regions_synced_at ?? null)}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Btn
                onClick={() => syncDriversMutation.mutate()}
                loading={syncDriversMutation.isPending}
                disabled={syncing}
              >
                Sync Drivers
              </Btn>
              <span className="text-xs text-slate-500">
                Last synced: {formatSyncedAt(data?.drivers_synced_at ?? null)}
              </span>
            </div>

            <Field
              label="Fallback company for unmatched drivers"
              hint="Team members whose Locate2u company doesn't match a company here are placed under this company; leave blank to skip them."
            >
              <Select
                value={overrideCompanyId}
                onChange={(e) => setOverrideCompanyId(e.target.value)}
              >
                <option value="">— skip unmatched —</option>
                {(companiesData?.companies ?? []).map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.company_name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          {syncMessage && <p className="text-sm text-emerald-700">{syncMessage}</p>}
          {data?.last_error && (
            <p className="text-sm text-red-600">Last sync error: {data.last_error}</p>
          )}

          <Btn
            variant="ghost"
            loading={disconnectMutation.isPending}
            onClick={() => {
              void (async () => {
                if (
                  await confirm({
                    title: 'Disconnect Locate2u',
                    body: 'This removes the stored credentials. Synced regions and drivers are kept.',
                    confirmLabel: 'Disconnect',
                    danger: true,
                  })
                )
                  disconnectMutation.mutate();
              })();
            }}
          >
            Disconnect
          </Btn>
        </div>
      )}

      <ErrorMessage error={error} />
    </div>
  );
}

function ColourField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | undefined;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value && HEX_RE.test(value) ? value : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-12 rounded border border-slate-200"
        />
        <TextInput value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      </div>
    </Field>
  );
}
